import { NgClass } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { getButtonDetails } from './breadcrumbs.constants';
import { MatButtonModule } from '@angular/material/button';
export interface ButtonDetails {
  name: string;
  isDisabled: boolean;
  route: string;
}

@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  imports: [NgClass, RouterModule, MatButtonModule],
  templateUrl: './breadcrumbs.component.html',
  styleUrl: './breadcrumbs.component.scss'
})
export class BreadcrumbsComponent {
  static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
  readonly home = { icon: 'pi pi-home', url: 'home' };
  menuItems: any[] = [];
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  isNavigationfromBreadcrumb = false;
  buttonDetails?: ButtonDetails[] = [];

  /**
   * Normalizes a URL by stripping query params and fragments
   */
  private normalizeUrl(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  ngOnInit(): void {
    // Initialize breadcrumb for current route on page load
    this.menuItems = this.createBreadcrumbs(this.activatedRoute.root, '', []);
    this.buttonDetails = getButtonDetails(this.normalizeUrl(this.router.url));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        const normalizedEventUrl = this.normalizeUrl(event.urlAfterRedirects);
        const alreadyPresentIndex = this.menuItems.findIndex(
          (breadcrumb: any) => this.normalizeUrl(breadcrumb.url) === normalizedEventUrl
        );

        if (alreadyPresentIndex !== -1) {
          this.menuItems.splice(alreadyPresentIndex + 1);
        } else {
          this.menuItems = this.createBreadcrumbs(this.activatedRoute.root, '', this.menuItems);
          this.menuItems = this.menuItems.slice(-5, this.menuItems.length)
        }
        this.buttonDetails = getButtonDetails(normalizedEventUrl);
      });
  }

  private createBreadcrumbs(route: ActivatedRoute, url: string = '#', breadcrumbs: any[] = []): any {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }
    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      if (child.snapshot.data[BreadcrumbsComponent.ROUTE_DATA_BREADCRUMB]?.alias) {
        let label = child.snapshot.data[BreadcrumbsComponent.ROUTE_DATA_BREADCRUMB]?.alias || child.snapshot.routeConfig?.children?.[0].data?.[BreadcrumbsComponent.ROUTE_DATA_BREADCRUMB]?.alias;
        //This is only for enrollments pages
        if (this.router.url.includes('enrollment')) {
          label += this.getEnrollmentType();
        } else if (this.router.url.includes('administration')) {
          label += this.getAdministrationType();
        } else if (this.router.url.includes('form-management')) {
          label += this.getFormManagementType();
        } 
        let isRouteAlreadyPresent = false;
        breadcrumbs.map((breadcrumb: any, index: number) => {
          if (breadcrumb.url === url) {
            isRouteAlreadyPresent = true;
          }
        });
        if (!(label === undefined || label === null) && !isRouteAlreadyPresent) {
          breadcrumbs.push({ label, url, data: child.snapshot.data });
        }
      }
      return this.createBreadcrumbs(child, url, breadcrumbs);
    }
  }
  redirectToRoute(menu: any, index: number) {
    if (index === this.menuItems.length - 1) {
      return;
    }
    this.menuItems.splice(index + 1);
    this.router.navigate([menu.url], menu.data);
  }

  getEnrollmentType() {
    const currentEnrollmentType = this.router.url.split('/').pop();
    switch (currentEnrollmentType) {
      case 'suppliers':
        return ' - Suppliers';
      case 'campaigns':
        return ' - Campaigns';
      case 'groups':
        return ' - Groups';
      case 'all':
        return ' - All Enrollments';
      case 'failedEnrollments':
        return ' - Failed Enrollments';
      default:
        return '';
    }
  }

  getAdministrationType() {
    const currentEnrollmentType = this.router.url.split('/').pop();
    if (this.router.url.includes("/edit-email-template/")) {
      return ' - Edit Email Template';
    }
    if (this.router.url.includes("/user-profile/")) {
      return ' - User Profile';
    }
    if (this.router.url.includes("/organization-profile/")) {
      return ' - Organization Profile';
    }
    switch (currentEnrollmentType) {
      case 'email-templates':
        return ' - Email Templates';
      case 'user-roles':
        return ' - User and Roles';
      case 'general-settings':
        return ' - General Settings';
      case 'banner-settings':
        return ' - Banner Settings';
      case 'notification-settings':
        return ' - Notification Settings';
      case 'appearance-settings':
        return ' - Appearance Settings';
      case 'attributes-configuration-settings':
        return ' - Attributes Configuration Settings';
      case 'onboarding-settings':
        return ' - Onboarding Settings';
      case 'settings':
        return ' - Settings';
      default:
        return '';
    }
  }
  getFormManagementType() {
    const currentEnrollmentType = this.router.url.split('/').pop();
    switch (currentEnrollmentType) {
      case 'form-request':
        return ' - Form Request';
      default:
        return '';
    }
  }
}
